import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { CallControls } from '../components/CallControls';
import { NotifyMe } from '../components/NotifyMe';
import { OrderFeedback } from '../components/OrderFeedback';

import {
  answerQuestion,
  fetchMyOrder,
  type ItemAnswer,
  type ItemQuestion,
  type MyOrder as Order,
} from '../lib/api';
import { buzz, chime } from '../lib/alert';
import { money } from '../lib/money';
import { useOzi } from '../state/ozi';
import { useSession } from '../state/session';

/**
 * A Shopper's order while it is on its way, and the place their Runner's questions arrive.
 *
 * When the Runner cannot find something, the question appears here with two answers, and
 * nobody's phone number changes hands (chosen by Anthony, 27 Sep 2026). The page asks the
 * server every few seconds, so the Shopper does not have to keep refreshing.
 *
 * Only the question itself is in the alert, and its words do not change while it waits: a
 * live region whose countdown ticked would be read out again every few seconds. The time
 * left is in minutes, next to the buttons, and says what happens if nobody answers.
 *
 * A new question also chimes and buzzes, once, for somebody who has put the phone down with the
 * page open. For a closed page, the Shopper can allow notifications here (`NotifyMe`).
 *
 * And Ozi asks it aloud (16 October 2026): "Sam can't find bananas. Say similar, or leave it
 * out", and listens for the answer, as well as the two buttons. Said once per question.
 */

/** What the Shopper said back to Ozi, or null when it was neither answer. */
export function spokenAnswer(text: string): ItemAnswer | null {
  const words = text.toLowerCase();
  if (/\b(leave|without|skip|forget|drop|don'?t (want|need|bother))\b/.test(words)) {
    return 'leave_out';
  }
  if (/\b(similar|something else|another|alternative|swap|replace|substitute|yes)\b/.test(words)) {
    return 'similar';
  }
  return null;
}

const POLL_MS = 5000;

const ANSWER_WORDS: Record<ItemAnswer, string> = {
  similar: 'bring something similar',
  leave_out: 'leave it out',
};

function whereItIs(order: Order): string {
  const runner = order.runnerName ?? 'Your Runner';
  switch (order.status) {
    case 'paid':
    case 'offered':
      return 'We are finding you a Runner.';
    case 'accepted':
      return `${runner} has your order and is going to the shop.`;
    case 'shopping':
      return `${runner} is doing your shopping now.`;
    case 'receipt_submitted':
      return `${runner} has paid at the till and will set off soon.`;
    case 'delivering':
      return `${runner} is on the way to you.`;
    case 'delivered':
    case 'completed':
      return 'Your shopping has been delivered.';
  }
}

/** "around 2:35pm", in the UK. */
function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Europe/London',
  });
}

function timeLeft(seconds: number): string {
  if (seconds < 60) return 'less than a minute';
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? 'about a minute' : `about ${minutes} minutes`;
}

export function MyOrder(): JSX.Element {
  const { shopper, restoring } = useSession();
  const ozi = useOzi();
  const [order, setOrder] = useState<Order | null>(null);
  const [questions, setQuestions] = useState<ItemQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [news, setNews] = useState('');
  const problemRef = useRef<HTMLParagraphElement>(null);
  const heard = useRef(new Set<string>());
  // The question Ozi is waiting for an answer to, and how to stop waiting.
  const listening = useRef<{ questionId: string; stop: () => void } | null>(null);
  const live = useRef({ ozi });
  live.current = { ozi };

  useEffect(
    () => () => {
      listening.current?.stop();
    },
    [],
  );

  const refresh = useCallback(async () => {
    try {
      const result = await fetchMyOrder();
      setOrder(result.order);
      const questions = result.questions ?? [];
      setQuestions(questions);
      const fresh = questions.filter(
        (question) => question.answer === null && !heard.current.has(question.id),
      );
      fresh.forEach((question) => heard.current.add(question.id));
      if (fresh.length > 0) {
        chime();
        buzz();
      }
      const first = fresh[0];
      if (first && result.order) askAloudRef.current(result.order, first);
      // Answered on the screen, or settled: Ozi stops waiting for the words.
      const waitingFor = listening.current;
      if (
        waitingFor &&
        !questions.some((q) => q.id === waitingFor.questionId && q.answer === null)
      ) {
        waitingFor.stop();
        listening.current = null;
      }
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'We could not check your order.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!shopper) return undefined;
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [shopper, refresh]);

  useEffect(() => {
    if (problem !== '') problemRef.current?.focus();
  }, [problem]);

  /** The question said aloud, with Ozi listening for "similar" or "leave it out". */
  const askAloudRef = useRef(askAloud);
  askAloudRef.current = askAloud;
  function askAloud(forOrder: Order, question: ItemQuestion, retried = false): void {
    const o = live.current.ozi;
    listening.current?.stop();
    const runner = forOrder.runnerName ?? 'Your Runner';
    const prompt = retried
      ? "Sorry, I didn't catch that. Say similar, or leave it out. Or press one of the buttons on the screen."
      : `${runner} can't find ${question.itemName}. Say 'similar' or 'leave it out'.`;
    const stop = o.listenFor(prompt, (text) => {
      listening.current = null;
      const value = spokenAnswer(text);
      if (value === null) {
        if (!retried) askAloud(forOrder, question, true);
        else void o.say('All right. The two buttons are on the screen whenever you are ready.');
        return;
      }
      void answerQuestion(forOrder.id, question.id, value)
        .then((result) => {
          setNews(result.message);
          void o.say(result.message);
        })
        .catch((failure: unknown) => {
          const words =
            failure instanceof Error ? failure.message : 'We could not send your answer.';
          setProblem(words);
          void o.say(words);
        })
        .finally(() => {
          void refresh();
        });
    });
    listening.current = { questionId: question.id, stop };
  }

  async function answer(question: ItemQuestion, value: ItemAnswer): Promise<void> {
    if (!order || busy) return;
    if (listening.current?.questionId === question.id) {
      listening.current.stop();
      listening.current = null;
    }
    setBusy(true);
    setProblem('');
    try {
      const result = await answerQuestion(order.id, question.id, value);
      setNews(result.message);
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'We could not send your answer.');
    } finally {
      setBusy(false);
      await refresh();
    }
  }

  if (restoring) {
    return (
      <p role="status" className="m-0">
        One moment.
      </p>
    );
  }

  if (!shopper) {
    return (
      <div className="space-y-6">
        <h1 className="text-display font-bold m-0">Your order</h1>
        <p className="m-0 max-w-xl">Sign in to see where your order has got to.</p>
        <Link to="/sign-in?next=/my-order" className="control bg-highlight text-on-accent">
          Sign in
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <p role="status" className="m-0">
        Checking your order.
      </p>
    );
  }

  const waiting = questions.filter((question) => question.answer === null);
  const settled = questions.filter((question) => question.answer !== null);

  return (
    <div className="space-y-8">
      <h1 className="text-display font-bold m-0">Your order</h1>

      <p role="status" className="m-0 min-h-control max-w-xl">
        {news}
      </p>

      {problem !== '' && (
        <p
          ref={problemRef}
          tabIndex={-1}
          role="alert"
          className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0 max-w-xl"
        >
          {problem}
        </p>
      )}

      {!order ? (
        <div className="space-y-4">
          <p className="m-0 max-w-xl">You have no order on its way at the moment.</p>
          <Link to="/shop" className="control bg-highlight text-on-accent">
            Find your shopping
          </Link>
        </div>
      ) : (
        <>
          {waiting.map((question) => (
            <section
              key={question.id}
              aria-labelledby={`question-${question.id}`}
              className="space-y-3 max-w-xl border-2 border-highlight rounded-2xl p-4"
            >
              <h2 id={`question-${question.id}`} className="text-lead font-bold m-0">
                A question from your Runner
              </h2>
              <p role="alert" className="m-0 text-lead">
                {order.runnerName ?? 'Your Runner'} cannot find {question.itemName}. What would you
                like them to do?
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  void answer(question, 'similar');
                }}
                className="control w-full bg-highlight text-on-accent text-lead disabled:opacity-70"
              >
                Bring something similar
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  void answer(question, 'leave_out');
                }}
                className="control w-full bg-paper text-ink text-lead disabled:opacity-70"
              >
                Leave it out
              </button>
              <p className="m-0">
                If we do not hear from you in {timeLeft(question.secondsLeft)}, they will leave it
                out, and you will not be charged for it. Nothing is ever swapped without asking you.
              </p>
            </section>
          ))}

          <NotifyMe />

          {order.runnerName && !['delivered', 'completed'].includes(order.status) && (
            <CallControls orderId={order.id} as="shopper" otherName={order.runnerName} />
          )}

          <section aria-labelledby="where-heading" className="space-y-3 max-w-xl">
            <h2 id="where-heading" className="text-lead font-bold">
              Where it has got to
            </h2>
            <p className="m-0">{whereItIs(order)}</p>
            {order.eta && (
              <p className="m-0 text-lead">
                Expected in {order.eta.words}, by around {clockTime(order.eta.byAt)}.
              </p>
            )}
            {order.eta && (
              <p className="m-0 extra">
                This is our best guess from where your order has got to, and it changes as your
                Runner goes. Shops can be busy, so it is never a promise to the minute.
              </p>
            )}
          </section>

          {/* An order coming in parts (ruling 60): every part's arrival time and door words. */}
          {order.split && (
            <section aria-labelledby="parts-heading" className="space-y-3 max-w-xl">
              <h2 id="parts-heading" className="text-lead font-bold">
                Your order comes in {order.split.of} parts
              </h2>
              <p className="m-0">{order.split.words}</p>
              <ul className="m-0 ps-6 space-y-3">
                {order.split.parts.map((part) => (
                  <li key={part.part ?? 0} className="space-y-1">
                    <p className="m-0 font-bold">
                      Part {part.part} of {part.of}:{' '}
                      {['delivered', 'completed'].includes(part.status)
                        ? 'delivered.'
                        : part.runnerName
                          ? `${part.runnerName} has it.`
                          : 'finding a Runner.'}
                    </p>
                    {part.eta && !['delivered', 'completed'].includes(part.status) && (
                      <p className="m-0">
                        Expected in {part.eta.words}, by around {clockTime(part.eta.byAt)}.
                      </p>
                    )}
                    {part.doorWord && part.runnerName && (
                      <p className="m-0">
                        {part.runnerName} will say: <strong>{part.doorWord}</strong>
                      </p>
                    )}
                    <p className="m-0 extra">
                      {part.items.map((item) => `${item.quantity} × ${item.name}`).join(', ')}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {order.doorWord &&
            order.runnerName &&
            !['delivered', 'completed'].includes(order.status) && (
              <section aria-labelledby="door-heading" className="space-y-3 max-w-xl">
                <h2 id="door-heading" className="text-lead font-bold">
                  Who is at the door
                </h2>
                <p className="m-0 text-lead">
                  {order.runnerName} will say: <strong>{order.doorWord}</strong>
                </p>
                <p className="m-0 extra">
                  Your Runner says these two words at your door, so you know it is them. If they do
                  not, you do not need to open the door.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    void ozi.say(
                      order.doorWordSentence ??
                        `Your Runner is ${order.runnerName}. At your door they will say ${order.doorWord}.`,
                    );
                  }}
                  className="control border-paper bg-paper/10 text-paper"
                >
                  Say the door words aloud
                </button>
              </section>
            )}

          {['delivered', 'completed'].includes(order.status) && (
            <OrderFeedback
              orderId={order.id}
              given={order.feedbackGiven ?? false}
              creditPence={order.feedbackCreditPence ?? 0}
            />
          )}

          <section aria-labelledby="list-heading" className="space-y-3 max-w-xl">
            <h2 id="list-heading" className="text-lead font-bold">
              What you asked for
            </h2>
            <ul className="m-0 ps-6 space-y-1">
              {order.items.map((item) => {
                const decided = settled.find((question) => question.orderItemId === item.id);
                return (
                  <li key={item.id}>
                    {item.quantity} × {item.name}
                    {decided?.answer &&
                      (decided.answeredBy === 'shopper'
                        ? `. Not on the shelf: you asked them to ${ANSWER_WORDS[decided.answer]}.`
                        : '. Not on the shelf, and we could not reach you in time, so it was left out. You are not charged for it.')}
                  </li>
                );
              })}
            </ul>
            <p className="m-0">
              About {money(order.totalEstimatePence)} altogether. You pay what the till says.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
